import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { isNil, uniq } from "lodash";
import moment from "moment";
import { useToasts } from "react-toast-notifications";
import { v4 as uuidv4 } from "uuid";

import { useUser } from "@components/user-context";
import {
  LoadingIndicator,
  PrimaryButton,
  SecondaryButton,
  Spinner,
} from "@components/v2";
import { FormattedNumberInput } from "@components/v2/inputs/formatted-number-input";
import { GET_OVERVIEW_METRICS_REPORT } from "@gql/overview-v2";
import {
  ADOPT_UNIVERSAL_BENCHMARKS,
  GET_ADOPTED_UNIVERSAL_BENCHMARKS_METADATA,
  GET_UNIVERSAL_BENCHMARKS_DAYS,
  GET_UNIVERSAL_BENCHMARKS_METADATA,
} from "@gql/universal-bentchmarks";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type AdoptUniversalBenchmarks,
  type AdoptUniversalBenchmarksVariables,
  type GetOverviewMetricsReport,
  type GetOverviewMetricsReportVariables,
  type GetUniversalBenchmarksDays,
  type GetUniversalBenchmarksDaysVariables,
} from "@north-beam/nb-client-side";
import {
  Currencies,
  formatting,
  todayLocalAsISODate,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { BenchmarkCard } from "../benchmark-targets/benchmark-card";
import { DEFAULT_BASELINE_FILTERS } from "../constants";
import {
  getDaysSelection,
  useAdoptableMetrics,
  useDaysSelection,
  useUniversalBenchmarksConfiguratorContext,
} from "../hooks";
import {
  type AdoptedMetrics,
  type AdoptedMetricsData,
  type BaselineFilters,
  type MetricPointWithDate,
} from "../types";
import {
  calculateBaselineDaysAverages,
  calculateMeanValueForDaysSelection,
  normalizeMetricValue,
  resolveBaseMetricsForGrossRev,
  roundNumberTwoDecimalPlaces,
} from "../utils";

import { DateSelect } from "./date-select";
import { MetricModeSelect } from "./metric-mode-select";

const DEFAULT_START_PAGE_BASELINE_FILTERS: BaselineFilters = {
  ...DEFAULT_BASELINE_FILTERS,
  discountsThreshold: {
    enabled: false,
    value: 0,
    method: "blended",
  },
};

const METRICS_WITH_SUPPORTED_MODE_SELECT = [
  "roas",
  "roasFt",
  "roasRtn",
  "cac",
  "cacFt",
  "cacRtn",
  "rev",
  "revFt",
  "revRtn",
];

interface Props {
  onClose: () => void;
  onComplete: () => void;
}

export const StartPage = ({ onClose, onComplete }: Props) => {
  const { addToast } = useToasts();
  const { user } = useUser();
  const {
    metadata,
    baselineFilters: {
      dateRange: initialDateRange,
      adjustment: initialAdjustment,
    },
    baselineMetrics,
    buildKpiMetrics,
  } = useUniversalBenchmarksConfiguratorContext();

  const skipSettingTargetBenchmark = useRef(!isNil(initialAdjustment));

  const [benchmarkAttributionModel, setBenchmarkAttributionModel] =
    useState<string>(initialAdjustment?.attributionModel ?? "northbeam_custom");
  const [benchmarkAccountingMode, setBenchmarkAccountingMode] =
    useState<INBAccountingMode>(initialAdjustment?.accountingMode ?? "cash");
  const [benchmarkAttributionWindow, setBenchmarkAttributionWindow] =
    useState<IMCAttributionWindowDays>(
      initialAdjustment?.attributionWindow ?? "infinity",
    );
  const [adoptedMetrics, setAdoptedMetrics] = useState<AdoptedMetrics[]>([
    {
      id: uuidv4(),
      metricId: initialAdjustment?.metricId ?? "roas",
      benchmarkFilters: [],
      kpiFilters: [],
    },
  ]);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [targetBenchmarkValue, setTargetBenchmarkValue] = useState(
    initialAdjustment?.value ?? 0,
  );
  const [targetBenchmarkDefaultValue, setTargetBenchmarkDefaultValue] =
    useState(initialAdjustment?.value ?? 0);
  const [targetBenchmarkBounds, setTargetBenchmarkBounds] = useState({
    min: 0,
    max: 0,
  });
  const [forceRerenderTargetValue, setForceRerenderTargetValue] = useState("0");

  useEffect(() => {
    setDateRange(initialDateRange);
  }, [initialDateRange]);

  useEffect(() => {
    if (initialAdjustment) {
      setTargetBenchmarkValue(initialAdjustment.value);
      setBenchmarkAttributionModel(initialAdjustment.attributionModel);
      setBenchmarkAccountingMode(initialAdjustment.accountingMode);
      setBenchmarkAttributionWindow(initialAdjustment.attributionWindow);
      setAdoptedMetrics([
        {
          id: uuidv4(),
          metricId: initialAdjustment.metricId,
          benchmarkFilters: [],
          kpiFilters: [],
        },
      ]);
    }
    skipSettingTargetBenchmark.current = !isNil(initialAdjustment);
  }, [initialAdjustment]);

  const dateRangeInterval = useMemo(
    () => parseDateRangeArgument(dateRange, todayLocalAsISODate()),
    [dateRange],
  );

  const adoptableBenchmarksMetrics = useAdoptableMetrics(
    benchmarkAttributionModel,
    benchmarkAttributionWindow,
    benchmarkAccountingMode,
  );

  const adoptedMetric = adoptedMetrics[0];

  const adoptedSalesMetric = useMemo(() => {
    return adoptableBenchmarksMetrics.metrics.find(
      (e) => e.id === adoptedMetric.metricId,
    )!;
  }, [adoptedMetric, adoptableBenchmarksMetrics]);

  const { data: baselineDaysData, loading: isBaselineDaysLoading } =
    useNorthbeamQuery<
      GetUniversalBenchmarksDays,
      GetUniversalBenchmarksDaysVariables
    >(GET_UNIVERSAL_BENCHMARKS_DAYS, {
      variables: {
        dateRange: dateRangeInterval,
        attributionModel: "northbeam_custom",
        accountingMode: "cash",
        attributionWindow: "infinity",
      },
    });

  const { data: kpiData, loading: isKpiDataLoading } = useNorthbeamQuery<
    GetOverviewMetricsReport,
    GetOverviewMetricsReportVariables
  >(GET_OVERVIEW_METRICS_REPORT, {
    variables: {
      attributionMethod: benchmarkAttributionModel,
      accountingMode: benchmarkAccountingMode,
      attributionWindow:
        benchmarkAccountingMode === "cash"
          ? "infinity"
          : benchmarkAttributionWindow,
      attributionWindows: [
        benchmarkAccountingMode === "cash"
          ? "infinity"
          : benchmarkAttributionWindow,
      ],
      timeGranularity: "daily",
      level: "campaign",
      dateRange: dateRangeInterval,
      metrics: uniq([
        ...resolveBaseMetricsForGrossRev(
          adoptedSalesMetric.requiredBaseMetrics,
        ),
        "rev",
        "spend",
        "txns",
      ]),
      filters: [],
      disableDateCompression: true,
    },
  });

  const isLoading = isBaselineDaysLoading || isKpiDataLoading;

  const baselineDays = useMemo(() => {
    return (baselineDaysData?.me.universalBenchmarksDays ??
      []) as MetricPointWithDate[];
  }, [baselineDaysData]);

  const adoptedKpiData = useMemo((): AdoptedMetricsData[] => {
    return [
      {
        metricId: adoptedMetric.id,
        status: isLoading ? "loading" : "done",
        data: kpiData?.me.overviewMetricsReport ?? null,
      },
    ];
  }, [adoptedMetric, kpiData, isLoading]);

  useEffect(() => {
    if (!baselineDaysData || !adoptedKpiData[0]) {
      return;
    }

    const baselineDays = baselineDaysData.me.universalBenchmarksDays;
    const cancelledOrders =
      baselineDaysData.me.universalBenchmarksCancelledOrders;

    const {
      averageSpend,
      averageDiscount,
      averageShipping,
      averageTaxes,
      averageRefunds,
      averageCancellations,
    } = calculateBaselineDaysAverages({
      baselineDays,
      cancelledOrders,
      baselineMetrics,
      ecomAccountingSettings: user.ecomAccountingSettings,
      profitableDayThresholdMethod: "blended",
    });

    const bFilters = {
      ...DEFAULT_START_PAGE_BASELINE_FILTERS,
      dateRange,
      cogs: {
        ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs,
        spend: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.spend,
          value: roundNumberTwoDecimalPlaces(averageSpend),
        },
        discounts: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.discounts,
          value: roundNumberTwoDecimalPlaces(averageDiscount),
        },
        shipping: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.shipping,
          value: roundNumberTwoDecimalPlaces(averageShipping),
        },
        taxes: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.taxes,
          value: roundNumberTwoDecimalPlaces(averageTaxes),
        },
        returns: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.returns,
          value: roundNumberTwoDecimalPlaces(
            averageRefunds + averageCancellations,
          ),
        },
      },
    };

    const calculateMeanValue = (filters: BaselineFilters) => {
      const dSelection = getDaysSelection({
        baselineMetrics,
        baselineDays,
        baselineFilters: filters,
        wizardStep: "adopt-benchmark",
        kpiMetricsData: [],
        adoptableKpiMetrics: adoptableBenchmarksMetrics,
        adoptedMetrics,
        adoptedKpiData,
        user,
        buildKpiMetrics,
      });

      return calculateMeanValueForDaysSelection({
        daysSelection: dSelection,
        metric: adoptedSalesMetric,
        rows: adoptedKpiData[0].data?.rows,
      });
    };

    const defaultValue = roundNumberTwoDecimalPlaces(
      calculateMeanValue(bFilters),
    );
    const minValue = calculateMeanValue({
      ...bFilters,
      adjustment: {
        dimension: "top",
        metricId: adoptedMetric.metricId,
        value: -Infinity,
        attributionModel: "northbeam_custom",
        accountingMode: "cash",
        attributionWindow: "infinity",
      },
    });
    const maxValue = calculateMeanValue({
      ...bFilters,
      adjustment: {
        dimension: "top",
        metricId: adoptedMetric.metricId,
        value: Infinity,
        attributionModel: "northbeam_custom",
        accountingMode: "cash",
        attributionWindow: "infinity",
      },
    });

    const minBound = calculateMeanValue({
      ...bFilters,
      adjustment: {
        dimension: "top",
        metricId: adoptedMetric.metricId,
        value: minValue + minValue * 0.3,
        attributionModel: "northbeam_custom",
        accountingMode: "cash",
        attributionWindow: "infinity",
      },
    });

    const maxBound = calculateMeanValue({
      ...bFilters,
      adjustment: {
        dimension: "top",
        metricId: adoptedMetric.metricId,
        value: maxValue - maxValue * 0.3,
        attributionModel: "northbeam_custom",
        accountingMode: "cash",
        attributionWindow: "infinity",
      },
    });

    setTargetBenchmarkDefaultValue(defaultValue);
    setTargetBenchmarkBounds({
      min: Math.min(minBound, defaultValue),
      max: Math.max(maxBound, defaultValue),
    });

    if (!skipSettingTargetBenchmark.current) {
      setTargetBenchmarkValue(defaultValue);
    }
  }, [
    baselineDaysData,
    baselineMetrics,
    dateRange,
    user.ecomAccountingSettings,
    adoptableBenchmarksMetrics,
    adoptedMetrics,
    adoptedSalesMetric,
    adoptedKpiData,
    adoptedMetric,
    buildKpiMetrics,
  ]);

  const baselineFilters = useMemo(() => {
    const bDays = baselineDaysData?.me.universalBenchmarksDays ?? [];
    const cancelledOrders =
      baselineDaysData?.me.universalBenchmarksCancelledOrders ?? [];

    const {
      averageSpend,
      averageDiscount,
      averageShipping,
      averageTaxes,
      averageRefunds,
      averageCancellations,
    } = calculateBaselineDaysAverages({
      baselineDays: bDays,
      cancelledOrders,
      baselineMetrics,
      ecomAccountingSettings: user.ecomAccountingSettings,
      profitableDayThresholdMethod: "blended",
    });

    return {
      ...DEFAULT_START_PAGE_BASELINE_FILTERS,
      dateRange,
      cogs: {
        ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs,
        spend: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.spend,
          value: roundNumberTwoDecimalPlaces(averageSpend),
        },
        discounts: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.discounts,
          value: roundNumberTwoDecimalPlaces(averageDiscount),
        },
        shipping: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.shipping,
          value: roundNumberTwoDecimalPlaces(averageShipping),
        },
        taxes: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.taxes,
          value: roundNumberTwoDecimalPlaces(averageTaxes),
        },
        returns: {
          ...DEFAULT_START_PAGE_BASELINE_FILTERS.cogs.returns,
          value: roundNumberTwoDecimalPlaces(
            averageRefunds + averageCancellations,
          ),
        },
      },
      adjustment: {
        dimension: "top",
        metricId: adoptedMetric.metricId,
        value: targetBenchmarkValue,
        attributionModel: benchmarkAttributionModel,
        accountingMode: benchmarkAccountingMode,
        attributionWindow:
          benchmarkAccountingMode === "cash"
            ? "infinity"
            : benchmarkAttributionWindow,
      },
    };
  }, [
    baselineDaysData,
    baselineMetrics,
    user.ecomAccountingSettings,
    dateRange,
    targetBenchmarkValue,
    adoptedMetric,
    benchmarkAttributionModel,
    benchmarkAccountingMode,
    benchmarkAccountingMode,
  ]);

  const daysSelection = useDaysSelection({
    baselineMetrics,
    baselineDays,
    baselineFilters,
    wizardStep: "adopt-benchmark",
    kpiMetricsData: [],
    adoptableKpiMetrics: adoptableBenchmarksMetrics,
    adoptedMetrics,
    adoptedKpiData,
    buildKpiMetrics,
  });

  const daysPercentBenchmarkWasHit = useMemo(() => {
    const row = adoptedKpiData[0].data?.rows[0];

    if (!row) {
      return 0;
    }

    const totalDays = row.metrics.length;
    let hitDays = 0;

    for (const metricPoint of row.metrics) {
      const metricValue = normalizeMetricValue(
        adoptedSalesMetric.calculate(metricPoint),
      );

      if (
        adoptedSalesMetric.isPositiveChangeGood &&
        metricValue >= targetBenchmarkValue
      ) {
        hitDays += 1;
      }

      if (
        !adoptedSalesMetric.isPositiveChangeGood &&
        metricValue <= targetBenchmarkValue
      ) {
        hitDays += 1;
      }
    }

    return hitDays / totalDays;
  }, [adoptedSalesMetric, adoptedKpiData, targetBenchmarkValue]);

  const { inputLeftIcon, inputRightIcon } = useMemo(() => {
    let inputLeftIcon = null;
    let inputRightIcon = null;

    if (adoptedSalesMetric.format === "percentage") {
      inputRightIcon = <span className="text-xl">%</span>;
    }

    if (adoptedSalesMetric.format === "multiplier") {
      inputRightIcon = <span className="text-xl">x</span>;
    }

    if (adoptedSalesMetric.format === "dollars") {
      const code = user.currencies.currentCurrency;
      inputLeftIcon = (
        <span className="text-xl">
          {Currencies.getAttribute(code, "symbol") ?? code}
        </span>
      );
    }

    return { inputLeftIcon, inputRightIcon };
  }, [user.currencies.currentCurrency, adoptedSalesMetric]);

  const [adoptUniversalBenchmarks, { loading: isAdoptingBenchmark }] =
    useNorthbeamMutation<
      AdoptUniversalBenchmarks,
      AdoptUniversalBenchmarksVariables
    >(ADOPT_UNIVERSAL_BENCHMARKS);

  const adoptBenchmark = useCallback(async () => {
    const dateRangeInterval = parseDateRangeArgument(
      baselineFilters.dateRange,
      todayLocalAsISODate(),
    );

    const selectedDays = daysSelection
      .filter((e) => !e.isExcluded)
      .map((e) => e.day);

    const metricIdsToAdopt = adoptedMetrics.flatMap((e) => {
      const baseId = e.metricId.replace(/Ft|Rtn/, "");
      return [baseId, `${baseId}Ft`, `${baseId}Rtn`];
    });

    const adoptedMetricIds = adoptableBenchmarksMetrics.metrics
      .filter((e) => metricIdsToAdopt.includes(e.id))
      .map((e) => e.id);

    await adoptUniversalBenchmarks({
      variables: {
        baselineFilters: {
          ...baselineFilters,
          dateRangeInterval,
        },
        selectedDays,
        metricIds: adoptedMetricIds,
      },
      refetchQueries: [
        { query: GET_ADOPTED_UNIVERSAL_BENCHMARKS_METADATA },
        { query: GET_UNIVERSAL_BENCHMARKS_METADATA },
      ],
      awaitRefetchQueries: true,
    });
  }, [
    baselineFilters,
    daysSelection,
    adoptableBenchmarksMetrics.metrics,
    adoptedMetrics,
  ]);

  const metricName = useMemo(() => {
    const name =
      adoptedSalesMetric.dashboardV2Name ||
      adoptedSalesMetric.metadata.overviewPageMetricName ||
      adoptedSalesMetric.metadata.name ||
      adoptedSalesMetric.name;

    return name.replace(/ \(New\)| \(Returning\)/, "");
  }, [adoptedSalesMetric]);

  const selectedDaysCount = daysSelection.filter((e) => !e.isExcluded).length;

  return (
    <motion.div
      exit={{ opacity: 0 }}
      className={clsx(
        "absolute left-0 top-0 bottom-0 right-0 bg-v2-glass-inverse backdrop-blur-sm z-1",
        "border border-v2-tertiary rounded flex items-center justify-center",
      )}
    >
      {isLoading && (
        <div
          className={clsx(
            "absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center",
            "bg-v2-glass-inverse z-1 pb-14",
          )}
        >
          <LoadingIndicator />
        </div>
      )}
      <div className="w-[960px]">
        <h1 className="text-4xl mb-12">Welcome to Profitability Benchmarks!</h1>
        <div className="flex gap-6">
          <div className="shrink-0 w-[200px]">
            <BenchmarkCard
              hideFilters
              showFtRtnMetrics
              adoptedMetrics={adoptedMetrics}
              adoptedMetric={adoptedMetrics[0]}
              adoptedMetricsData={adoptedKpiData[0]}
              adoptableBenchmarksMetrics={adoptableBenchmarksMetrics.metrics}
              daysSelection={daysSelection}
              benchmarkAttributionModel={benchmarkAttributionModel}
              benchmarkAttributionWindow={benchmarkAttributionWindow}
              benchmarkAccountingMode={benchmarkAccountingMode}
              onUpdate={(value) => {
                setAdoptedMetrics(
                  adoptedMetrics.map((e) =>
                    e.id === adoptedMetric.id ? value : e,
                  ),
                );
                skipSettingTargetBenchmark.current = false;
              }}
              onDelete={() => {
                setAdoptedMetrics(
                  adoptedMetrics.filter((e) => e.id !== adoptedMetric.id),
                );
                skipSettingTargetBenchmark.current = false;
              }}
              onBenchmarkAttributionModelChange={(val) => {
                setBenchmarkAttributionModel(val);
                skipSettingTargetBenchmark.current = false;
              }}
              onBenchmarkAttributionWindowChange={(val) => {
                setBenchmarkAttributionWindow(val);
                skipSettingTargetBenchmark.current = false;
              }}
              onBenchmarkAccountingModeChange={(val) => {
                setBenchmarkAccountingMode(val);
                if (val === "accrual") {
                  setBenchmarkAttributionWindow("1");
                } else {
                  setBenchmarkAttributionWindow("infinity");
                }
                skipSettingTargetBenchmark.current = false;
              }}
            />
          </div>
          <div>
            <div className="font-bold text-3xl mb-2 flex items-center gap-2">
              <div className="shrink-0">
                Your basic{" "}
                {METRICS_WITH_SUPPORTED_MODE_SELECT.includes(
                  adoptedMetric.metricId,
                ) && (
                  <>
                    <MetricModeSelect
                      value={
                        adoptedMetric.metricId.endsWith("Ft")
                          ? "new"
                          : adoptedMetric.metricId.endsWith("Rtn")
                          ? "returning"
                          : "blended"
                      }
                      onChange={(val) => {
                        const id = adoptedMetric.metricId.replace(
                          /(Ft|Rtn)$/,
                          "",
                        );
                        let newMetric = null;

                        if (val === "blended") {
                          newMetric = adoptableBenchmarksMetrics.metrics.find(
                            (metric) => metric.id === id,
                          );
                        } else if (val === "new") {
                          newMetric = adoptableBenchmarksMetrics.metrics.find(
                            (metric) => metric.id === `${id}Ft`,
                          );
                        } else {
                          newMetric = adoptableBenchmarksMetrics.metrics.find(
                            (metric) => metric.id === `${id}Rtn`,
                          );
                        }

                        if (newMetric) {
                          setAdoptedMetrics([
                            {
                              id: uuidv4(),
                              metricId: newMetric.id,
                              benchmarkFilters: [],
                              kpiFilters: [],
                            },
                          ]);
                          skipSettingTargetBenchmark.current = false;
                        }
                      }}
                    />{" "}
                  </>
                )}
                benchmark is
              </div>
              <div className="truncate">{metricName}</div>
              <FormattedNumberInput
                key={forceRerenderTargetValue}
                wrapperClassName="h-[46px] w-36"
                className="text-3xl font-sans"
                leftIcon={inputLeftIcon}
                rightIcon={inputRightIcon}
                disabled={isLoading || selectedDaysCount === 0}
                defaultValue={
                  targetBenchmarkDefaultValue *
                  (adoptedSalesMetric.format === "percentage" ? 100 : 1)
                }
                value={
                  targetBenchmarkValue *
                  (adoptedSalesMetric.format === "percentage" ? 100 : 1)
                }
                onChange={(val) => {
                  if (!isNil(val)) {
                    let valToSet =
                      val /
                      (adoptedSalesMetric.format === "percentage" ? 100 : 1);

                    if (valToSet < targetBenchmarkBounds.min) {
                      valToSet = targetBenchmarkBounds.min;
                      addToast(
                        `Minimum allowed value is ${formatting.formatMetric(
                          targetBenchmarkBounds.min,
                          adoptedSalesMetric.format,
                          {
                            code: user.currencies.currentCurrency,
                            mantissa: 2,
                          },
                        )}`,
                        { appearance: "warning", autoDismiss: true },
                      );
                    }

                    if (valToSet > targetBenchmarkBounds.max) {
                      valToSet = targetBenchmarkBounds.max;
                      addToast(
                        `Maximum allowed value is ${formatting.formatMetric(
                          targetBenchmarkBounds.max,
                          adoptedSalesMetric.format,
                          {
                            code: user.currencies.currentCurrency,
                            mantissa: 2,
                          },
                        )}`,
                        { appearance: "warning", autoDismiss: true },
                      );
                    }

                    setTargetBenchmarkValue(valToSet);
                    setForceRerenderTargetValue(Math.random().toString());
                  }
                }}
              />
            </div>
            <div className="text-xl mb-6 space-x-1.5">
              <span>Based on</span>
              <DateSelect
                dateRange={dateRange}
                minDate={metadata.minDate ?? moment().format("YYYY-MM-DD")}
                onChange={(val) => {
                  setDateRange(val);
                  skipSettingTargetBenchmark.current = false;
                }}
              />
              <span>of spend data, you’ve hit your benchmark</span>
              <span className="font-semibold">
                {formatting.formatPercentage(daysPercentBenchmarkWasHit, {
                  mantissa: 0,
                })}
              </span>
              <span>of the time.</span>
            </div>
            {!isLoading && selectedDaysCount === 0 ? (
              <div>
                <p className="text-v2-secondary">
                  Your basic benchmark can't be calculated at this time! Try
                  changing date range or
                </p>
                <PrimaryButton
                  disabled={isLoading || isAdoptingBenchmark}
                  onClick={onClose}
                >
                  SET YOUR SMART BENCHMARK!
                </PrimaryButton>
              </div>
            ) : (
              <SecondaryButton
                disabled={isLoading || isAdoptingBenchmark}
                onClick={async () => {
                  await adoptBenchmark();
                  onComplete();
                }}
              >
                {isAdoptingBenchmark && <Spinner />}
                {initialAdjustment
                  ? "UPDATE YOUR BASIC BENCHMARK"
                  : "ADOPT YOUR BASIC BENCHMARK"}
              </SecondaryButton>
            )}
          </div>
        </div>
        <div className="py-12 relative mb-6">
          <div className="absolute left-0 w-[calc(50%-16px)] top-1/2 border-b border-v2-tertiary"></div>
          <div className="absolute right-0 w-[calc(50%-16px)] top-1/2 border-b border-v2-tertiary"></div>
          <div
            className={clsx(
              "absolute left-[calc(50%-8px)] top-[calc(50%-8px)]",
              "text-xs font-semibold",
            )}
          >
            OR
          </div>
        </div>
        <div className="flex gap-6 px-6">
          <div>
            <h3 className="font-bold text-3xl mb-3">Smart Benchmark</h3>
            <div className="mb-6 text-xl">
              To provide a more tailored benchmark, we need a little more
              information about your business. Don’t worry, it’s only 4 easy
              steps!
            </div>
            <PrimaryButton
              className="w-full"
              disabled={isLoading || isAdoptingBenchmark}
              onClick={onClose}
            >
              GET STARTED!
            </PrimaryButton>
          </div>
          <div className="shrink-0 w-[508px] h-[301px] border border-v2-tertiary rounded-lg overflow-hidden">
            <iframe
              src="https://www.loom.com/embed/f68f392d4d0946bf82d061cc8143a352"
              title="Profitability Benchmarks"
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
